import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import api from '../../services/Api';
import { BsCamera } from 'react-icons/bs';
import toast from 'react-hot-toast';
import { showError, showLoading, showSuccess } from '../Ui/ToastNotification';
// import { useSelector } from 'react-redux';

const IndustryModal = ({
  isOpen,
  onClose,
  mode = 'add',
  initialData = {},
  onSuccess,
}) => {
  // const { userData } = useSelector((state) => state.auth);
  // const userEmail = userData?.email;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    tags: [],
    image: null,
    updateId: null,
    previewImage: null,
    newTag: '',
  });

  useEffect(() => {
    if (
      mode === 'update' &&
      initialData &&
      Object.keys(initialData).length > 0
    ) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        status: initialData.isActive ? 'active' : 'inactive',
        tags: initialData.tags || [],
        image: null,
        updateId: initialData._id || null,
        previewImage: initialData.imageUrl || initialData.previewImage || null,
        newTag: '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        status: 'active',
        tags: [],
        image: null,
        updateId: null,
        previewImage: null,
        newTag: '',
      });
    }
  }, [initialData, mode, isOpen]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      event.target.value = '';
      return;
    }

    // Validate file size (1MB)
    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      showError(`File size (${sizeInMB}MB) exceeds 5MB limit.`);
      event.target.value = '';
      return;
    }

    // Preview
    const previewUrl = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      image: file,
      previewImage: previewUrl,
    }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = formData.newTag.trim().replace(/,/g, '');
      if (tag && !formData.tags.includes(tag)) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, tag],
          newTag: '',
        }));
      }
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showError('Industry name is required');
      return;
    }

    const loadingToastId = showLoading('Saving your data. Please wait...');

    try {
      let imageUrl = formData.previewImage;

      if (formData.image && formData.image instanceof File) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('file', formData.image);

          const uploadResponse = await api.post(`/upload/file`, imageFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          imageUrl = uploadResponse.data.secure_url || uploadResponse.data.url;

          if (!imageUrl) {
            console.error('Upload response:', uploadResponse.data);
            showError('Failed to get image URL from upload response');
            toast.dismiss(loadingToastId);
            return;
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast.dismiss(loadingToastId);
          showError('Failed to upload image. Please try again.');
          return;
        }
      }

      if (formData.updateId) {
        const updateData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          isActive: formData.status === 'active',
          tags: formData.tags,
          imageUrl,
        };

        await api.patch(
          `/industryListing/industries/${formData.updateId}`,
          updateData,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
      } else {
        const createData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          isActive: formData.status === 'active',
          tags: formData.tags,
          imageUrl,
        };

        await api.post('/industryListing/industries', createData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      toast.dismiss(loadingToastId);
      showSuccess('The industry has been saved successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.dismiss(loadingToastId);

      if (error.response) {
        const { status, data } = error.response;
        console.error('Server error response:', data);

        if (status === 400) {
          const errorMessage =
            data.message ||
            data.error ||
            'Invalid data. Please check your inputs.';
          showError(errorMessage);
        } else if (status === 413) {
          showError('File too large. Please choose a smaller image.');
        } else {
          showError(
            'There was an issue saving the industry. Please try again.',
          );
        }
      } else {
        showError('Network error. Please check your connection and try again.');
      }

      console.error('Error saving industry:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-8 sm:ml-64 w-[80%] max-w-2xl rounded-lg shadow-lg overflow-y-auto font-nohemi font-light">
        <div className="items-center justify-center w-full mx-auto">
          <div className="mb-6">
            {/* Photo Upload Section */}
            <div className="flex justify-center mb-6">
              <div className="flex flex-col items-center">
                <label className="cursor-pointer">
                  <div className="flex items-center justify-center w-24 h-24 overflow-hidden bg-gray-100 border-2 border-gray-300 border-dashed rounded-full">
                    {formData.previewImage ? (
                      <img
                        src={formData.previewImage}
                        alt="Preview"
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          console.error(
                            'Image failed to load:',
                            formData.previewImage,
                          );
                          console.error('Error event:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <BsCamera className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <p className="text-base font-semibold text-[#202224] mt-2 text-center">
                    Upload Photo
                  </p>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Max size: 5MB
                  </p>
                </label>
                {formData.image && (
                  <p className="mt-1 text-sm text-green-600">
                    {formData.image.name} selected
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
              {/* Industry Name */}
              <div className="md:col-span-1">
                <label
                  htmlFor="industryName"
                  className="block mb-2 text-base font-semibold"
                >
                  Industry Name *
                </label>
                <input
                  id="industryName"
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Required field (e.g., 'Technology,' 'Healthcare')"
                  className="w-full p-3 text-[#202224] border border-gray-300 rounded-md bg-[#F5F6FA] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status */}
              <div className="md:col-span-1">
                <label className="block mb-2 text-base font-semibold ">
                  Status
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center text-base font-semibold">
                    <input
                      type="radio"
                      checked={formData.status === 'active'}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, status: 'active' }))
                      }
                      className="mr-2 accent-black"
                    />
                    Active
                  </label>
                  <label className="flex items-center text-base font-semibold">
                    <input
                      type="radio"
                      checked={formData.status === 'inactive'}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, status: 'inactive' }))
                      }
                      className="mr-2 accent-black"
                    />
                    Inactive
                  </label>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <label
                htmlFor="description"
                className="block mb-2 text-base font-semibold"
              >
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="A brief summary of the industry."
                className="w-full p-3 border border-gray-300 rounded-md bg-[#F5F6FA] text-[#202224] text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
              />
            </div>

            {/* Tags Section */}
            <div>
              <label
                htmlFor="tags"
                className="block mb-2 text-base font-semibold"
              >
                Tags/Keywords
              </label>
              <input
                type="text"
                value={formData.newTag}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, newTag: e.target.value }))
                }
                onKeyDown={handleAddTag}
                placeholder="Type a tag and press Enter or comma"
                className="w-full p-3 text-[#202224] text-sm border border-gray-300 rounded-md bg-[#F5F6FA] mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="bg-[#F5F6FA] p-3 rounded-md min-h-[50px] border border-gray-200">
                {formData.tags.length > 0 ? (
                  formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 mb-2 mr-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-gray-500 hover:text-red-500 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No tags added yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex gap-4 mt-8">
            <button
              onClick={handleSubmit}
              className="flex-1 py-3 text-white transition-colors bg-black rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
            >
              {mode === 'add' ? 'Create Industry' : 'Update Industry'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

IndustryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.string,
  initialData: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default IndustryModal;
